import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpContext, HttpResponse } from "@angular/common/http";
import { MITPService } from "./mitp.service";
import { SKIP_ERROR_TOAST } from "./http-context-tokens";
import { ProtocolContentDraft } from "../interfaces/protocols/protocol-content-draft";
import { BulkImportOptions, BulkImportResult } from "../interfaces/protocols/protocol-bulk-import";
import { Observable, Subject, shareReplay } from "rxjs";
import { ProtocolAttachment } from "../entities/protocol-attachment";
import { ProtocolUpdate } from "../interfaces/protocols/protocol-update";
import { Protocol } from "../interfaces/protocols/protocol";
import { ProtocolSafety } from "../entities/protocols/protocol-safety";
import { User } from "../interfaces/users/user";
import { ISharingRecipientsSearchResults } from "../interfaces/protocol/isharing-recipients-search-results";
import { ProtocolAccessToken } from "../interfaces/protocols/protocol-access-token";
import { ProtocolWarning } from "../modules/protocols/classes/protocol-warning";
import { ProtocolApproval } from "../modules/protocols/entities/protocol-approval";
import { ProtocolsResult } from "../modules/protocols/entities/protocols-result";
import { NewProtocolRequest } from "../modules/protocols/entities/new-protocol-request";
import { ProtocolUsageStatistics } from "../interfaces/protocols/protocol-usage-statistics";
import { ProtocolLinksResponse } from "../interfaces/protocols/protocol-links";

@Injectable({
	providedIn: "root",
})
export class ProtocolService {
	private httpClient = inject(HttpClient);
	private mitp = inject(MITPService);

	private protocolsCache$: Observable<Protocol[]> | null = null;
	private invalidateProtocols$ = new Subject<void>();

	private myProtocolsCache$: Observable<Protocol[]> | null = null;
	private invalidateMyProtocols$ = new Subject<void>();

	getPublishedProtocol(id: number): Observable<string> {
		return this.httpClient.get(this.mitp.Protocols() + `/${id}/published`, {
			responseType: "text",
		});
	}

	importPdf(formData: FormData, type: string = "text"): Observable<ProtocolContentDraft> {
		return this.httpClient.post<ProtocolContentDraft>(
			this.mitp.Protocols() + `/import/pdf?outputType=${type}`,
			formData
		);
	}

	/**
	 * Creates a draft protocol from one PDF: the backend reads its title, codification and content with AI.
	 * The caller reports errors file by file, so the global error toast is skipped.
	 */
	importProtocolFromPdf(file: File, options: BulkImportOptions): Observable<BulkImportResult> {
		const formData = new FormData();
		formData.append("file", file);
		for (const [field, ids] of Object.entries(options) as [keyof BulkImportOptions, number[]][]) {
			ids.forEach((id) => formData.append(`${field}[]`, String(id)));
		}
		return this.httpClient.post<BulkImportResult>(this.mitp.Protocols() + "/import/bulk", formData, {
			context: new HttpContext().set(SKIP_ERROR_TOAST, true),
		});
	}

	getAttachments(protocolId: number): Observable<ProtocolAttachment[]> {
		return this.httpClient.get<ProtocolAttachment[]>(this.mitp.Protocols() + `/${protocolId}/attachments`);
	}

	addAttachment(protocolId: number, formData: FormData): Observable<HttpResponse<ProtocolAttachment>> {
		return this.httpClient.post<ProtocolAttachment>(
			this.mitp.Protocols() + `/${protocolId}/attachments`,
			formData,
			{
				observe: "response",
			}
		);
	}

	renameAttachment(protocolId: number, attachmentId: number, newLabel: string): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(
			this.mitp.Protocols() + `/${protocolId}/attachments/${attachmentId}/rename`,
			{ label: newLabel },
			{
				observe: "response",
			}
		);
	}

	replaceAttachment(protocolId: number, attachmentId: number, formData: FormData): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(
			this.mitp.Protocols() + `/${protocolId}/attachments/${attachmentId}/replace`,
			formData,
			{
				observe: "response",
			}
		);
	}

	/**
	 * Download a protocol attachment
	 * @param protocolId The protocol id
	 * @param attachmentId The attachment id
	 *
	 * @param t The timestamp to avoid caching: without this parameter,
	 * the browser will cache the first downloaded file. If the user replaces
	 * it and downloads the same file again, the browser will not send a request
	 * to the server, but will use the cached file.
	 *
	 * @returns The attachment as an array buffer
	 */
	downloadProtocolAttachment(protocolId: number, attachmentId: number): Observable<HttpResponse<ArrayBuffer>> {
		return this.httpClient.get(
			this.mitp.Protocols() + `/${protocolId}/attachments/${attachmentId}?t=${new Date()}`,
			{
				observe: "response",
				responseType: "arraybuffer",
			}
		);
	}

	shareProtocolAttachment(
		protocolId: number,
		attachmentId: number,
		email: string,
		isSecureEmail: boolean
	): Observable<HttpResponse<null>> {
		const formData = new FormData();
		formData.append("email", email);
		formData.append("isSecureEmail", isSecureEmail.toString());
		return this.httpClient.post<null>(
			this.mitp.Protocols() + `/${protocolId}/attachments/${attachmentId}/share`,
			formData,
			{
				observe: "response",
			}
		);
	}

	deleteAttachment(protocolId: number, attachmentId: number): Observable<HttpResponse<null>> {
		return this.httpClient.delete<null>(this.mitp.Protocols() + `/${protocolId}/attachments/${attachmentId}`, {
			observe: "response",
		});
	}

	toggleAttachmentSharingStatus(
		protocolId: number,
		attachmentId: number,
		newStatus: boolean
	): Observable<HttpResponse<null>> {
		let status = "disable";
		if (newStatus) {
			status = "enable";
		}
		return this.httpClient.put<null>(
			this.mitp.Protocols() + `/${protocolId}/attachments/${attachmentId}/share/${status}`,
			null,
			{
				observe: "response",
			}
		);
	}

	getVersion(protocolId: number, updateId: number): Observable<string> {
		return this.httpClient.get<string>(this.mitp.Protocols() + `/${protocolId}/updates/${updateId}/view`);
	}

	compareVersion(protocolId: number, updateId: number, version: string): Observable<string> {
		return this.httpClient.get<string>(
			this.mitp.Protocols() + `/${protocolId}/updates/${updateId}/compare/${version}`
		);
	}

	getProtocolUpdates(protocolId: number): Observable<ProtocolUpdate[]> {
		return this.httpClient.get<ProtocolUpdate[]>(this.mitp.Protocols() + `/${protocolId}/updates`);
	}

	getPendingApprovalRequets(): Observable<Protocol[]> {
		return this.httpClient.get<Protocol[]>(this.mitp.Protocols() + `/my/approval-requests`);
	}

	isProtocolSafe(protocolId: number): Observable<ProtocolSafety> {
		return this.httpClient.get<ProtocolSafety>(this.mitp.Protocols() + `/${protocolId}/safety`);
	}

	postSecurityApproval(protocolId: number): Observable<HttpResponse<object>> {
		return this.httpClient.put(this.mitp.Protocols() + `/${protocolId}/security/fingerprint`, null, {
			observe: "response",
		});
	}

	exportQualityProtocols(): Observable<HttpResponse<ArrayBuffer>> {
		return this.httpClient.get(this.mitp.Quality() + `/protocols/export?t=${Date.now()}`, {
			observe: "response",
			responseType: "arraybuffer",
		});
	}

	createProtocol(data: any): Observable<HttpResponse<object>> {
		return this.httpClient.post(this.mitp.Protocols(), data, {
			observe: "response",
		});
	}

	getProtocols(): Observable<Protocol[]> {
		if (!this.protocolsCache$) {
			this.protocolsCache$ = this.httpClient.get<Protocol[]>(this.mitp.Protocols()).pipe(shareReplay(1));
			this.invalidateProtocols$.subscribe(() => (this.protocolsCache$ = null));
		}
		return this.protocolsCache$;
	}

	invalidateProtocolsCache(): void {
		this.invalidateProtocols$.next();
	}

	getRecentProtocols(limitToDepartment: boolean): Observable<Protocol[]> {
		return this.httpClient.get<Protocol[]>(
			this.mitp.Protocols() + `/recent?limitToDepartment=${limitToDepartment}`
		);
	}

	getAllProtocols(currentPage: number, maxPerPage: number): Observable<ProtocolsResult> {
		if (currentPage < 1) {
			currentPage = 1;
		}
		return this.httpClient.get<ProtocolsResult>(this.mitp.Protocols() + "/all", {
			params: {
				currentPage: currentPage,
				maxPerPage: maxPerPage,
			},
		});
	}

	getQualityProtocols(): Observable<Protocol[]> {
		return this.httpClient.get<Protocol[]>(this.mitp.Quality() + "/protocols");
	}

	/** `skipErrorToast` leaves the error message to the caller, e.g. to explain a protocol the user may not read. */
	getProtocolById(id: number, skipErrorToast = false): Observable<Protocol> {
		return this.httpClient.get<Protocol>(this.mitp.Protocols() + `/${id}`, {
			context: new HttpContext().set(SKIP_ERROR_TOAST, skipErrorToast),
		});
	}

	getProtocolByToken(token: string): Observable<any> {
		return this.httpClient.get<any>(this.mitp.PublicProtocols(), {
			params: { token },
		});
	}

	getMyProtocols(): Observable<Protocol[]> {
		if (!this.myProtocolsCache$) {
			this.myProtocolsCache$ = this.httpClient
				.get<Protocol[]>(this.mitp.Protocols() + "/my")
				.pipe(shareReplay(1));
			this.invalidateMyProtocols$.subscribe(() => (this.myProtocolsCache$ = null));
		}
		return this.myProtocolsCache$;
	}

	invalidateMyProtocolsCache(): void {
		this.invalidateMyProtocols$.next();
	}

	getAvailableAuthors(): Observable<User[]> {
		return this.httpClient.get<User[]>(this.mitp.Institutes() + `/authors`);
	}

	getDraftProtocol(id: number): Observable<ProtocolContentDraft> {
		return this.httpClient.get<ProtocolContentDraft>(this.mitp.Protocols() + `/${id}/draft`);
	}

	updateProtocolContent(id: number, body: ProtocolContentDraft): Observable<HttpResponse<object>> {
		return this.httpClient.put(this.mitp.Protocols() + `/${id}/draft`, body, { observe: "response" });
	}

	updateProtocolMode(id: number, mode: "normal" | "advanced"): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Protocols() + `/${id}/mode/${mode}`, null, {
			observe: "response",
		});
	}

	updateSharedProtocols(share): Observable<HttpResponse<object>> {
		return this.httpClient.put(this.mitp.Protocols() + "/my/share", share, {
			observe: "response",
		});
	}

	enableSharing(protocolId: number, isEnabled: boolean): Observable<HttpResponse<null>> {
		const value = isEnabled ? "enable" : "disable";
		return this.httpClient.put<null>(this.mitp.Protocols() + `/${protocolId}/share/status/${value}`, null, {
			observe: "response",
		});
	}

	editSharingMode(protocolId: number, mode: number): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Protocols() + `/${protocolId}/share/mode/${mode}`, null, {
			observe: "response",
		});
	}

	searchProtocolSharingRecipients(keyword: string): Observable<ISharingRecipientsSearchResults> {
		return this.httpClient.get<ISharingRecipientsSearchResults>(
			this.mitp.Protocols() + `/share/search?keyword=${keyword}`
		);
	}

	addShareProtocolWithZone(protocolId: number, zoneId: number): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(this.mitp.Protocols() + `/${protocolId}/share/zones/${zoneId}`, null, {
			observe: "response",
		});
	}

	removeShareProtocolWithZone(protocolId: number, zoneId: number): Observable<HttpResponse<null>> {
		return this.httpClient.delete<null>(this.mitp.Protocols() + `/${protocolId}/share/zones/${zoneId}`, {
			observe: "response",
		});
	}

	updateProtocolLabel(id: number, label: string): Observable<object> {
		return this.httpClient.put(this.mitp.Protocols() + `/${id}/label`, {
			label: label,
		});
	}

	notifyApprovers(protocolId: number, approvers: User[]): Observable<HttpResponse<object>> {
		return this.httpClient.put(
			this.mitp.Protocols() + `/${protocolId}/request-approval/${approvers.map((e) => e.id).join("-")}`,
			null,
			{
				observe: "response",
			}
		);
	}

	addProtocolAuthor(protocolId: number, userId: number, category: string): Observable<HttpResponse<object>> {
		return this.httpClient.post(this.mitp.Protocols() + `/${protocolId}/${category}/${userId}`, null, {
			observe: "response",
		});
	}

	deleteProtocolAuthor(protocolId: number, userId: number, category: string): Observable<HttpResponse<object>> {
		return this.httpClient.delete(this.mitp.Protocols() + `/${protocolId}/${category}/${userId}`, {
			observe: "response",
		});
	}

	updateProtocolError(protocolId: number, errorId: number): Observable<HttpResponse<object>> {
		return this.httpClient.put(this.mitp.Protocols() + `/${protocolId}/errors/${errorId}`, null, {
			observe: "response",
		});
	}

	validateSecurityFingerprint(protocolId: number): Observable<HttpResponse<object>> {
		return this.httpClient.put(this.mitp.Protocols() + `/${protocolId}/fingerprint`, null, { observe: "response" });
	}

	deleteProtocol(protocolId: number): Observable<HttpResponse<object>> {
		return this.httpClient.delete(this.mitp.Protocols() + `/${protocolId}`, { observe: "response" });
	}

	initiateProtocol(protocolId: number, updates: any): Observable<HttpResponse<object>> {
		return this.httpClient.post(
			this.mitp.Protocols() + `/${protocolId}/init`,
			{ updates: updates },
			{ observe: "response" }
		);
	}

	archiveProtocol(protocolId: number): Observable<HttpResponse<object>> {
		return this.httpClient.put(this.mitp.Protocols() + `/${protocolId}/archives`, null, { observe: "response" });
	}

	unarchiveProtocol(protocolId: number): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Protocols() + `/${protocolId}/unarchive`, null, {
			observe: "response",
		});
	}

	updateProtocolRisk(id: number, risk: number): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Protocols() + `/${id}/risk/${risk}`, null, {
			observe: "response",
		});
	}

	disableProtocol(protocolId: number): Observable<HttpResponse<object>> {
		return this.httpClient.put(this.mitp.Protocols() + `/${protocolId}/disable`, null, { observe: "response" });
	}

	publishProtocol(protocolId: number, type: string, notes: string): Observable<HttpResponse<object>> {
		return this.httpClient.post(
			this.mitp.Protocols() + `/${protocolId}/publish/${type}`,
			{ notes: notes },
			{ observe: "response" }
		);
	}

	updateProtocolCodification(protocolId: number, codification: string): Observable<HttpResponse<object>> {
		return this.httpClient.put(
			this.mitp.Protocols() + `/${protocolId}/codification`,
			{ codification: codification },
			{ observe: "response" }
		);
	}

	downloadProtocol(protocolId: number, version: string, format: string): Observable<HttpResponse<ArrayBuffer>> {
		return this.httpClient.get(this.mitp.Protocols() + `/${protocolId}/download/${version}/${format}`, {
			observe: "response",
			responseType: "arraybuffer",
		});
	}

	getProtocolAccessTokens(protocolId: number): Observable<ProtocolAccessToken[]> {
		return this.httpClient.get<ProtocolAccessToken[]>(this.mitp.Protocols() + `/${protocolId}/tokens`);
	}

	createProtocolAccessToken(protocolId: number): Observable<HttpResponse<ProtocolAccessToken>> {
		return this.httpClient.post<ProtocolAccessToken>(this.mitp.Protocols() + `/${protocolId}/tokens`, null, {
			observe: "response",
		});
	}

	addInstituteToProtocolShare(protocolId: number, instituteId: number): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(
			this.mitp.Protocols() + `/${protocolId}/share/institutes/${instituteId}`,
			null,
			{ observe: "response" }
		);
	}

	removeInstituteFromProtocolShare(protocolId: number, instituteId: number): Observable<HttpResponse<null>> {
		return this.httpClient.delete<null>(this.mitp.Protocols() + `/${protocolId}/share/institutes/${instituteId}`, {
			observe: "response",
		});
	}

	postProtocolError(protocolId: number, error: string, notes: string): Observable<HttpResponse<object>> {
		return this.httpClient.post(
			this.mitp.Protocols() + `/${protocolId}/errors`,
			{
				error: error,
				notes: notes,
			},
			{ observe: "response" }
		);
	}

	transferManager(user1Id: number, user2Id: number, removeManager: boolean): Observable<HttpResponse<object>> {
		return this.httpClient.put(
			this.mitp.Protocols() + "/transfer/managers",
			{
				current: user1Id,
				new: user2Id,
				remove: removeManager,
			},
			{ observe: "response" }
		);
	}

	getProtocolLinks(): Observable<ProtocolLinksResponse> {
		return this.httpClient.get<ProtocolLinksResponse>(this.mitp.Protocols() + "/statistics/links");
	}

	getMyProtocolLinks(): Observable<ProtocolLinksResponse> {
		return this.httpClient.get<ProtocolLinksResponse>(this.mitp.Protocols() + "/statistics/links/my");
	}

	shareProtocolByEmail(protocolId: number, email: string): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(
			this.mitp.Protocols() + `/${protocolId}/share/email`,
			{ email: email },
			{
				observe: "response",
			}
		);
	}

	createWarning(protocol: Protocol, warning: ProtocolWarning): Observable<ProtocolWarning> {
		return this.httpClient.post<ProtocolWarning>(this.mitp.Protocols() + `/${protocol.id}/warnings`, warning);
	}

	validateWarning(protocol: Protocol, warning: ProtocolWarning): Observable<ProtocolWarning> {
		return this.httpClient.put<ProtocolWarning>(
			this.mitp.Protocols() + `/${protocol.id}/warnings/${warning.id}/validate`,
			warning
		);
	}

	/**
	 * Validate the medical step for a protocol when the quality circuit is WorkflowProtocolManagementQuality
	 * @param protocolId
	 * @param notes
	 */
	validateMedicalStep(protocolId: number, notes: string): Observable<ProtocolApproval> {
		return this.httpClient.post<ProtocolApproval>(this.mitp.Protocols() + `/${protocolId}/validate/medical`, {
			notes: notes,
		});
	}

	/**
	 * Validate the quality step for a protocol when the quality circuit is WorkflowProtocolManagementQuality
	 * @param protocolId
	 */
	validateQualityStep(protocolId: number): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(this.mitp.Protocols() + `/${protocolId}/validate/quality`, null, {
			observe: "response",
		});
	}

	deleteApproval(protocolId: number, approvalId: number): Observable<null> {
		return this.httpClient.delete<null>(this.mitp.Protocols() + `/${protocolId}/approvals/${approvalId}`);
	}

	translate(protocolId: number): Observable<string> {
		return this.httpClient.get(this.mitp.Protocols() + `/${protocolId}/translate`, {
			responseType: "text",
		});
	}

	sendNewProtocolRequest(keyword: string, recipientIds: number[]): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(
			this.mitp.Protocols() + "/requests/new-protocol",
			{ keyword, recipientIds },
			{ observe: "response" }
		);
	}

	getMyNewProtocolRequest(): Observable<NewProtocolRequest[]> {
		return this.httpClient.get<NewProtocolRequest[]>(this.mitp.Protocols() + "/requests/new-protocol/my");
	}

	cancelNewProtocolRequest(requestId: number): Observable<HttpResponse<null>> {
		return this.httpClient.delete<null>(this.mitp.Protocols() + `/requests/new-protocol/${requestId}`, {
			observe: "response",
		});
	}

	getUsageStatistics(): Observable<ProtocolUsageStatistics> {
		return this.httpClient.get<ProtocolUsageStatistics>(this.mitp.Protocols() + "/statistics/usage");
	}
}
